import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useSendAdminMessageMutation } from '@/apis/extended/admin/adminWorkspaceApi';
import type { LoanAdminInsights } from '@/types/LoanApplicationType';

export type MessageLoanFile = {
  _id: string;
  address?: string;
  flowProfile?: string;
  insights?: LoanAdminInsights;
};

type TemplateKey = 'finish' | 'resign' | 'upload' | 'blank';

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string;
  firstName: string;
  email?: string;
  loanFiles?: MessageLoanFile[];
  /** Preselect a loan file (opened from that file). */
  applicationId?: string | null;
  initialTemplate?: TemplateKey;
};

const TEMPLATE_LABELS: Record<TemplateKey, string> = {
  finish: 'Finish your application',
  resign: 'Please re-sign',
  upload: 'Upload documents',
  blank: 'Blank message',
};

function fileLabel(f: MessageLoanFile) {
  const kind = f.flowProfile === 'refinance' ? 'Refinance' : 'Purchase';
  return f.address ? `${kind} · ${f.address}` : `${kind} loan file`;
}

function draftFor(
  key: TemplateKey,
  firstName: string,
  file?: MessageLoanFile
): { subject: string; body: string } {
  const missing = file?.insights?.documents.missing.map((m) => m.label) ?? [];
  const where = file?.address ? ` for ${file.address}` : '';
  switch (key) {
    case 'finish':
      return {
        subject: 'Finish your loan application',
        body: `Hi ${firstName},\n\nYour loan application${where} is almost done. Open it to finish the remaining sections; it picks up right where you left off.\n\nReply to this email if anything is unclear.`,
      };
    case 'resign':
      return {
        subject: 'Please re-sign your loan application',
        body: `Hi ${firstName},\n\nSome answers on your loan application${where} changed after you signed it. Please review it and sign again so we can keep it moving.`,
      };
    case 'upload':
      return {
        subject: 'Documents needed for your loan',
        body: `Hi ${firstName},\n\nTo keep your loan${where} moving, please upload:\n${
          missing.length
            ? missing.map((m) => `• ${m}`).join('\n')
            : '• (list the documents here)'
        }\n\nYou can upload them from the Documents step of your application.`,
      };
    default:
      return { subject: '', body: `Hi ${firstName},\n\n` };
  }
}

/** Write to a person from their page or a loan file, starting from a template. */
export function AdminMessageDialog({
  open,
  onClose,
  userId,
  firstName,
  email,
  loanFiles = [],
  applicationId,
  initialTemplate,
}: Props) {
  const [send, { isLoading }] = useSendAdminMessageMutation();
  const [fileId, setFileId] = useState<string>('');
  const [template, setTemplate] = useState<TemplateKey>('blank');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const selectedFile = useMemo(
    () => loanFiles.find((f) => f._id === fileId),
    [loanFiles, fileId]
  );

  // A sensible starting template for the file's state.
  useEffect(() => {
    if (!open) return;
    const startFile = applicationId ?? loanFiles[0]?._id ?? '';
    setFileId(startFile);
    const file = loanFiles.find((f) => f._id === startFile);
    const guess: TemplateKey =
      initialTemplate ??
      (file?.insights?.attestation === 'needs_resign'
        ? 'resign'
        : file?.insights?.documents.missing.length &&
          file.insights.attestation === 'signed'
        ? 'upload'
        : file
        ? 'finish'
        : 'blank');
    setTemplate(guess);
    const d = draftFor(guess, firstName, file);
    setSubject(d.subject);
    setBody(d.body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyTemplate = (key: TemplateKey, file = selectedFile) => {
    setTemplate(key);
    const d = draftFor(key, firstName, file);
    setSubject(d.subject);
    setBody(d.body);
  };

  const submit = async () => {
    try {
      const res = await send({
        userId,
        subject: subject.trim(),
        body: body.trim(),
        applicationId: fileId || null,
      }).unwrap();
      if (res.status === 'sent') toast.success(`Sent to ${email || firstName}`);
      else toast.warning(`Saved, but the email was ${res.status}`);
      onClose();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Could not send the message');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Message {firstName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {email ? (
            <Typography variant="body2" color="text.secondary">
              Sends an email to {email}. It's logged on their page.
            </Typography>
          ) : (
            <Alert severity="warning">This person has no email address.</Alert>
          )}
          {loanFiles.length > 0 && (
            <TextField
              select
              label="About"
              value={fileId}
              onChange={(e) => {
                const next = loanFiles.find((f) => f._id === e.target.value);
                setFileId(e.target.value);
                applyTemplate(template, next);
              }}
              size="small"
            >
              <MenuItem value="">Nothing specific</MenuItem>
              {loanFiles.map((f) => (
                <MenuItem key={f._id} value={f._id}>
                  {fileLabel(f)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(Object.keys(TEMPLATE_LABELS) as TemplateKey[]).map((k) => (
              <Chip
                key={k}
                label={TEMPLATE_LABELS[k]}
                color={template === k ? 'primary' : 'default'}
                variant={template === k ? 'filled' : 'outlined'}
                onClick={() => applyTemplate(k)}
              />
            ))}
          </Stack>
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={7}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={isLoading || !email || !subject.trim() || !body.trim()}
        >
          {isLoading ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
